import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Switch,
  FormControlLabel,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  GripVertical,
  ChevronUp,
  ChevronDown,
  Save,
  RotateCcw,
  FileText,
  Plus,
  Trash2,
  Edit2,
  Download,
  FolderOpen,
  Building2,
  ExternalLink,
  Layers,
  Search,
  LayoutGrid,
  Link as LinkIcon,
  Video,
  Image as ImageIcon,
  Type,
} from 'lucide-react';
import api from '../services/api';

export type ModuleType = 'system' | 'custom_html' | 'custom_banner' | 'custom_links' | 'custom_video';

export interface CustomLinkItem {
  id: string;
  title: string;
  subtitle?: string;
  url: string;
}

export interface LandingModule {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  order: number;
  type?: ModuleType;
  grid_columns?: 12 | 8 | 6 | 4;
  custom_title?: string;
  custom_subtitle?: string;
  content_html?: string;
  banner_url?: string;
  banner_link_url?: string;
  banner_cta_text?: string;
  video_url?: string;
  links?: CustomLinkItem[];
}

export const DEFAULT_LANDING_MODULES: LandingModule[] = [
  {
    id: 'hero_slider',
    name: 'ภาพสไลด์ข่าวประชาสัมพันธ์ (Hero Carousel)',
    description: 'สไลด์ภาพแบนเนอร์ข่าวประชาสัมพันธ์ 5 อันดับล่าสุด พร้อมปุ่มอ่านข่าวเต็ม',
    enabled: true,
    order: 1,
    type: 'system',
    grid_columns: 12,
  },
  {
    id: 'stats_kpi',
    name: 'ข้อมูลสถิติภาพรวมและภาคเรียนปัจจุบัน (KPI Metrics)',
    description: 'แถบระบุภาคเรียน/ปีการศึกษา และ 4 การ์ดสถิติ (นศ.ฝึกสอน, โรงเรียน, อ.นิเทศก์, วิจัย)',
    enabled: true,
    order: 2,
    type: 'system',
    grid_columns: 12,
  },
  {
    id: 'latest_news',
    name: 'ข่าวประชาสัมพันธ์และประกาศล่าสุด (10 อันดับแรก)',
    description: 'รายการการ์ดข่าวประกาศล่าสุด 10 รายการ พร้อมปุ่มดูข่าวทั้งหมด',
    enabled: true,
    order: 3,
    type: 'system',
    grid_columns: 8,
  },
  {
    id: 'practicum_calendar',
    name: 'ปฏิทินและกำหนดการฝึกประสบการณ์ฯ (Practicum Calendar)',
    description: 'ไทม์ไลน์และกำหนดการกิจกรรมการฝึกประสบการณ์วิชาชีพครู',
    enabled: true,
    order: 4,
    type: 'system',
    grid_columns: 4,
  },
  {
    id: 'download_resources',
    name: 'คลังเอกสาร คู่มือ และแบบฟอร์มดาวน์โหลด',
    description: 'รายการเอกสาร คู่มือ ระเบียบ และแบบฟอร์มดาวน์โหลดแยกตามหมวดหมู่',
    enabled: true,
    order: 5,
    type: 'system',
    grid_columns: 6,
  },
  {
    id: 'partner_schools',
    name: 'เครือข่ายสถานศึกษาฝึกประสบการณ์วิชาชีพครู',
    description: 'รายชื่อสถานศึกษาเครือข่ายความร่วมมือ และปุ่มดูทำเนียบโรงเรียนทั้งหมด',
    enabled: true,
    order: 6,
    type: 'system',
    grid_columns: 6,
  },
];

const resolveFileUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `/api/v1${url}`;
  if (!url.startsWith('/')) return `/api/v1/uploads/${url}`;
  return url;
};

const MODULE_TYPE_INFO: Record<ModuleType, { label: string; icon: any; color: string }> = {
  system: { label: 'โมดูลระบบมาตรฐาน', icon: Layers, color: '#1E40AF' },
  custom_html: { label: 'ข้อความ/เนื้อหาอิสระ (Rich Text)', icon: Type, color: '#0284C7' },
  custom_banner: { label: 'แบนเนอร์ประชาสัมพันธ์พิเศษ', icon: ImageIcon, color: '#D97706' },
  custom_links: { label: 'การ์ดลิงก์ทางลัดด่วน (Quick Links)', icon: LinkIcon, color: '#059669' },
  custom_video: { label: 'วิดีโอแนะนำ (Video/Embed)', icon: Video, color: '#7C3AED' },
};

const GRID_COLUMN_OPTIONS = [
  { value: 12, label: '12 คอลัมน์ (เต็มแถว 100%)', short: '100% (เต็มแถว)' },
  { value: 8, label: '8 คอลัมน์ (กว้างพิเศษ 66.7%)', short: '66% (กว้างพิเศษ)' },
  { value: 6, label: '6 คอลัมน์ (ครึ่งแถว/วางคู่ 50%)', short: '50% (วางคู่กัน)' },
  { value: 4, label: '4 คอลัมน์ (ด้านข้าง 33.3%)', short: '33% (ด้านข้าง)' },
];

const LandingPageManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [modules, setModules] = useState<LandingModule[]>(DEFAULT_LANDING_MODULES);
  const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
  const [savingModules, setSavingModules] = useState(false);

  // Module dialog state (Add / Edit)
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<LandingModule | null>(null);
  const [moduleForm, setModuleForm] = useState<{
    name: string;
    description: string;
    type: ModuleType;
    grid_columns: 12 | 8 | 6 | 4;
    custom_title: string;
    custom_subtitle: string;
    content_html: string;
    banner_url: string;
    banner_link_url: string;
    banner_cta_text: string;
    video_url: string;
    links_text: string;
  }>({
    name: '',
    description: '',
    type: 'custom_html',
    grid_columns: 12,
    custom_title: '',
    custom_subtitle: '',
    content_html: '',
    banner_url: '',
    banner_link_url: '',
    banner_cta_text: 'อ่านรายละเอียดเพิ่มเติม',
    video_url: '',
    links_text: '',
  });

  // Resources state
  const [resources, setResources] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [resourceSearch, setResourceSearch] = useState('');
  const [selectedCatFilter, setSelectedCatFilter] = useState('ALL');
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<any | null>(null);
  const [resourceFormData, setResourceFormData] = useState({
    title: '',
    description: '',
    category_id: '',
  });
  const [resourceFile, setResourceFile] = useState<File | null>(null);
  const [savingResource, setSavingResource] = useState(false);

  // Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any | null>(null);
  const [catFormData, setCatFormData] = useState({ name: '', description: '' });

  // Schools state
  const [schools, setSchools] = useState<any[]>([]);
  const [schoolSearch, setSchoolSearch] = useState('');

  // UI feedback
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [settingsRes, resourcesRes, catRes, portalRes] = await Promise.all([
          api.get('/settings').catch(() => ({ data: {} })),
          api.get('/resources').catch(() => ({ data: [] })),
          api.get('/resources/categories').catch(() => ({ data: [] })),
          api.get('/research/public').catch(() => ({ data: {} })),
        ]);

        // Load modules configuration
        if (settingsRes.data?.landing_page_modules) {
          try {
            const parsed =
              typeof settingsRes.data.landing_page_modules === 'string'
                ? JSON.parse(settingsRes.data.landing_page_modules)
                : settingsRes.data.landing_page_modules;
            if (Array.isArray(parsed) && parsed.length > 0) {
              setModules(parsed);
            }
          } catch (e) {
            console.warn('Failed to parse landing_page_modules:', e);
          }
        }

        setResources(resourcesRes.data || []);
        setCategories(catRes.data || []);

        let schoolList = [];
        try {
          const schRes = await api.get('/schools/public');
          if (Array.isArray(schRes.data) && schRes.data.length > 0) {
            schoolList = schRes.data;
          }
        } catch (e) {}
        if (schoolList.length === 0) {
          schoolList = portalRes.data?.schools || [];
        }
        setSchools(schoolList);
      } catch (err: any) {
        console.error('Failed to load landing page management data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Drag and drop handlers
  const handleDragStart = (idx: number) => {
    setDraggedIdx(idx);
  };

  const handleDragOver = (e: React.DragEvent, targetIdx: number) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === targetIdx) return;
    const reordered = [...modules];
    const item = reordered.splice(draggedIdx, 1)[0];
    reordered.splice(targetIdx, 0, item);
    // Update order indices
    const updated = reordered.map((mod, i) => ({ ...mod, order: i + 1 }));
    setModules(updated);
    setDraggedIdx(targetIdx);
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
  };

  // Move up/down
  const handleMove = (idx: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= modules.length) return;
    const reordered = [...modules];
    const item = reordered.splice(idx, 1)[0];
    reordered.splice(targetIdx, 0, item);
    const updated = reordered.map((mod, i) => ({ ...mod, order: i + 1 }));
    setModules(updated);
  };

  // Toggle enabled
  const handleToggleModule = (id: string) => {
    setModules((prev) =>
      prev.map((m) => (m.id === id ? { ...m, enabled: !m.enabled } : m))
    );
  };

  // Change module grid columns
  const handleChangeColumns = (id: string, cols: 12 | 8 | 6 | 4) => {
    setModules((prev) =>
      prev.map((m) => (m.id === id ? { ...m, grid_columns: cols } : m))
    );
  };

  // Open module dialog for adding or editing
  const handleOpenAddModule = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setEditingModule(null);
    setModuleForm({
      name: '',
      description: '',
      type: 'custom_html',
      grid_columns: 12,
      custom_title: '',
      custom_subtitle: '',
      content_html: '',
      banner_url: '',
      banner_link_url: '',
      banner_cta_text: 'อ่านรายละเอียดเพิ่มเติม',
      video_url: '',
      links_text: '',
    });
    setModuleDialogOpen(true);
  };

  const handleOpenEditModule = (mod: LandingModule) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setEditingModule(mod);
    const linksText = (mod.links || [])
      .map((l) => `${l.title} | ${l.url} | ${l.subtitle || ''}`)
      .join('\n');
    setModuleForm({
      name: mod.name || '',
      description: mod.description || '',
      type: mod.type || 'system',
      grid_columns: mod.grid_columns || 12,
      custom_title: mod.custom_title || '',
      custom_subtitle: mod.custom_subtitle || '',
      content_html: mod.content_html || '',
      banner_url: mod.banner_url || '',
      banner_link_url: mod.banner_link_url || '',
      banner_cta_text: mod.banner_cta_text || 'อ่านรายละเอียดเพิ่มเติม',
      video_url: mod.video_url || '',
      links_text: linksText,
    });
    setModuleDialogOpen(true);
  };

  const handleSaveModuleForm = () => {
    if (!moduleForm.name.trim()) {
      alert('กรุณาระบุชื่อโมดูล');
      return;
    }

    // Parse links if type is custom_links
    let parsedLinks: CustomLinkItem[] = [];
    if (moduleForm.type === 'custom_links' && moduleForm.links_text) {
      parsedLinks = moduleForm.links_text
        .split('\n')
        .map((line, i) => {
          const parts = line.split('|').map((s) => s.trim());
          if (!parts[0]) return null;
          return {
            id: `link-${i + 1}`,
            title: parts[0],
            url: parts[1] || '#',
            subtitle: parts[2] || '',
          };
        })
        .filter(Boolean) as CustomLinkItem[];
    }

    if (editingModule) {
      // Update existing module
      setModules((prev) =>
        prev.map((m) =>
          m.id === editingModule.id
            ? {
                ...m,
                name: moduleForm.name,
                description: moduleForm.description,
                grid_columns: moduleForm.grid_columns,
                custom_title: moduleForm.custom_title,
                custom_subtitle: moduleForm.custom_subtitle,
                content_html: moduleForm.content_html,
                banner_url: moduleForm.banner_url,
                banner_link_url: moduleForm.banner_link_url,
                banner_cta_text: moduleForm.banner_cta_text,
                video_url: moduleForm.video_url,
                links: parsedLinks.length > 0 ? parsedLinks : m.links,
              }
            : m
        )
      );
    } else {
      // Create new custom module
      const newModule: LandingModule = {
        id: `custom_${Date.now()}`,
        name: moduleForm.name,
        description: moduleForm.description,
        enabled: true,
        order: modules.length + 1,
        type: moduleForm.type,
        grid_columns: moduleForm.grid_columns,
        custom_title: moduleForm.custom_title,
        custom_subtitle: moduleForm.custom_subtitle,
        content_html: moduleForm.content_html,
        banner_url: moduleForm.banner_url,
        banner_link_url: moduleForm.banner_link_url,
        banner_cta_text: moduleForm.banner_cta_text,
        video_url: moduleForm.video_url,
        links: parsedLinks,
      };
      setModules((prev) => [...prev, newModule]);
    }
    setModuleDialogOpen(false);
  };

  const handleDeleteCustomModule = (id: string, name: string) => {
    if (!window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการลบโมดูล "${name}"?`)) return;
    setModules((prev) => {
      const filtered = prev.filter((m) => m.id !== id);
      return filtered.map((m, i) => ({ ...m, order: i + 1 }));
    });
  };

  // Save modules configuration to system settings
  const handleSaveModules = async () => {
    try {
      setSavingModules(true);
      await api.put('/settings', {
        landing_page_modules: JSON.stringify(modules),
      });
      setSnackbar({
        open: true,
        message: 'บันทึกการจัดเรียงโมดูลและเค้าโครงหน้าแรกเรียบร้อยแล้ว',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Save modules error:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.message || 'บันทึกการตั้งค่าไม่สำเร็จ',
        severity: 'error',
      });
    } finally {
      setSavingModules(false);
    }
  };

  // Reset to default modules
  const handleResetModules = () => {
    if (window.confirm('คุณต้องการรีเซ็ตลำดับโมดูลกลับเป็นค่าเริ่มต้นของระบบใช่หรือไม่?')) {
      setModules(DEFAULT_LANDING_MODULES);
    }
  };

  // --------------------------------------------------------------------------
  // RESOURCE HANDLERS
  // --------------------------------------------------------------------------
  const handleOpenResourceDialog = (resItem?: any) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (resItem) {
      setEditingResource(resItem);
      setResourceFormData({
        title: resItem.title || '',
        description: resItem.description || '',
        category_id: resItem.category_id || '',
      });
    } else {
      setEditingResource(null);
      setResourceFormData({
        title: '',
        description: '',
        category_id: categories[0]?.id || '',
      });
    }
    setResourceFile(null);
    setResourceDialogOpen(true);
  };

  const handleSaveResource = async () => {
    if (!resourceFormData.title.trim()) {
      alert('กรุณากรอกชื่อเอกสาร');
      return;
    }
    if (!editingResource && !resourceFile) {
      alert('กรุณาเลือกไฟล์ที่ต้องการอัปโหลด');
      return;
    }

    try {
      setSavingResource(true);
      const fd = new FormData();
      fd.append('title', resourceFormData.title);
      fd.append('description', resourceFormData.description);
      fd.append('category_id', resourceFormData.category_id);
      if (resourceFile) {
        fd.append('file', resourceFile);
      }

      if (editingResource) {
        await api.put(`/resources/${editingResource.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/resources/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setResourceDialogOpen(false);
      const resData = await api.get('/resources');
      setResources(resData.data || []);
      setSnackbar({ open: true, message: 'บันทึกข้อมูลเอกสารเรียบร้อยแล้ว', severity: 'success' });
    } catch (err: any) {
      alert(err.response?.data?.message || 'บันทึกเอกสารไม่สำเร็จ');
    } finally {
      setSavingResource(false);
    }
  };

  const handleDeleteResource = async (id: string, title: string) => {
    if (!window.confirm(`คุณต้องการลบเอกสาร "${title}" หรือไม่?`)) return;
    try {
      await api.delete(`/resources/${id}`);
      setResources((prev) => prev.filter((r) => r.id !== id));
      setSnackbar({ open: true, message: 'ลบเอกสารเรียบร้อยแล้ว', severity: 'success' });
    } catch (err: any) {
      alert(err.response?.data?.message || 'ลบเอกสารไม่สำเร็จ');
    }
  };

  // --------------------------------------------------------------------------
  // CATEGORY HANDLERS
  // --------------------------------------------------------------------------
  const handleOpenCategoryDialog = (cat?: any) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (cat) {
      setEditingCategory(cat);
      setCatFormData({ name: cat.name || '', description: cat.description || '' });
    } else {
      setEditingCategory(null);
      setCatFormData({ name: '', description: '' });
    }
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!catFormData.name.trim()) {
      alert('กรุณากรอกชื่อหมวดหมู่');
      return;
    }
    try {
      if (editingCategory) {
        await api.put(`/resources/categories/${editingCategory.id}`, catFormData);
      } else {
        await api.post('/resources/categories', catFormData);
      }
      setCategoryDialogOpen(false);
      const catData = await api.get('/resources/categories');
      setCategories(catData.data || []);
      setSnackbar({ open: true, message: 'บันทึกหมวดหมู่เรียบร้อยแล้ว', severity: 'success' });
    } catch (err: any) {
      alert(err.response?.data?.message || 'บันทึกหมวดหมู่ไม่สำเร็จ');
    }
  };

  const handleDeleteCategory = async (id: string, name: string) => {
    if (!window.confirm(`คุณต้องการลบหมวดหมู่ "${name}" หรือไม่?`)) return;
    try {
      await api.delete(`/resources/categories/${id}`);
      setCategories((prev) => prev.filter((c) => c.id !== id));
      setSnackbar({ open: true, message: 'ลบหมวดหมู่เรียบร้อยแล้ว', severity: 'success' });
    } catch (err: any) {
      alert(err.response?.data?.message || 'ลบหมวดหมู่ไม่สำเร็จ');
    }
  };

  // Filters for resources
  const filteredResources = resources.filter((r) => {
    const matchesCat = selectedCatFilter === 'ALL' || String(r.category_id) === String(selectedCatFilter);
    const matchesSearch =
      !resourceSearch.trim() ||
      (r.title && r.title.toLowerCase().includes(resourceSearch.toLowerCase())) ||
      (r.description && r.description.toLowerCase().includes(resourceSearch.toLowerCase()));
    return matchesCat && matchesSearch;
  });

  // Filters for schools
  const filteredSchools = schools.filter((s) => {
    return (
      !schoolSearch.trim() ||
      (s.name && s.name.toLowerCase().includes(schoolSearch.toLowerCase())) ||
      (s.province && s.province.toLowerCase().includes(schoolSearch.toLowerCase())) ||
      (s.district && s.district.toLowerCase().includes(schoolSearch.toLowerCase()))
    );
  });

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>กำลังโหลดข้อมูลระบบบริหารหน้าแรก...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header Banner */}
      <Box sx={{ mb: 3.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Layers size={28} color="#1E40AF" />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', fontFamily: "'Prompt', sans-serif" }}>
              ระบบบริหารจัดการหน้าแรกเว็บไซต์ (Landing Page Builder)
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
            จัดเรียงลำดับโมดูลแบบ Drag & Drop, เพิ่มโมดูลเนื้อหาใหม่, ปรับแต่งจำนวนคอลัมน์ CSS Grid Layout และจัดการคลังเอกสารดาวน์โหลด
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ExternalLink size={16} />}
            href="/"
            target="_blank"
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            เปิดดูหน้าแรกจริง
          </Button>
        </Box>
      </Box>

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2.5, boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab icon={<LayoutGrid size={18} />} iconPosition="start" label="จัดเรียงโมดูล & CSS Grid" sx={{ fontWeight: 700 }} />
          <Tab icon={<FolderOpen size={18} />} iconPosition="start" label="คลังเอกสาร & คู่มือดาวน์โหลด" sx={{ fontWeight: 700 }} />
          <Tab icon={<Building2 size={18} />} iconPosition="start" label="ทำเนียบสถานศึกษาเครือข่าย" sx={{ fontWeight: 700 }} />
        </Tabs>
      </Paper>

      {/* ========================================================================= */}
      {/* TAB 0: MODULES DRAG & DROP & CSS GRID BUILDER */}
      {/* ========================================================================= */}
      {activeTab === 0 && (
        <Box>
          {/* Action Toolbar */}
          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2.5, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  โครงสร้างการจัดวางหน้าแรก (โมดูลทั้งหมด {modules.length} รายการ)
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  สามารถลากสลับตำแหน่ง (Drag & Drop), เลือกลำดับ, ปรับจำนวนคอลัมน์ (12 = เต็มแถว, 6 = ครึ่งแถววางคู่กัน), และเพิ่มโมดูลใหม่ได้อิสระ
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Plus size={16} />}
                  onClick={handleOpenAddModule}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  เพิ่มโมดูลใหม่
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<RotateCcw size={16} />}
                  onClick={handleResetModules}
                  sx={{ borderRadius: 2, color: '#64748B' }}
                >
                  รีเซ็ตค่าเริ่มต้น
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={savingModules ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                  onClick={handleSaveModules}
                  disabled={savingModules}
                  sx={{ borderRadius: 2, fontWeight: 700, px: 3, boxShadow: '0 4px 14px rgba(30,64,175,0.3)' }}
                >
                  {savingModules ? 'กำลังบันทึก...' : 'บันทึกโครงสร้างหน้าแรก'}
                </Button>
              </Stack>
            </Box>
          </Paper>

          {/* Visual CSS Grid Layout Simulator / Summary */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2.5, bgcolor: '#F8FAFC', border: '1px dashed #CBD5E1' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', mb: 1 }}>
              จำลองการจัดวางแถวและคอลัมน์ (CSS Grid Preview):
            </Typography>
            <Grid container spacing={1.5}>
              {modules.filter((m) => m.enabled).map((m) => {
                const cols = m.grid_columns || 12;
                return (
                  <Grid size={{ xs: 12, md: cols }} key={m.id}>
                    <Box
                      sx={{
                        p: 1.2,
                        borderRadius: 1.5,
                        bgcolor: cols === 12 ? 'rgba(30, 64, 175, 0.08)' : cols === 8 ? 'rgba(2, 132, 199, 0.1)' : cols === 6 ? 'rgba(5, 150, 105, 0.1)' : 'rgba(217, 119, 6, 0.1)',
                        border: '1px solid',
                        borderColor: cols === 12 ? '#BFDBFE' : cols === 8 ? '#BAE6FD' : cols === 6 ? '#A7F3D0' : '#FDE68A',
                        textAlign: 'center',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        color: '#0F172A',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <span>#{m.order} {m.name}</span>
                      <Chip
                        label={`${cols}/12`}
                        size="small"
                        sx={{ height: 18, fontSize: '0.68rem', fontWeight: 700 }}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          {/* Modules Drag List */}
          <Stack spacing={2}>
            {modules.map((mod, index) => {
              const typeInfo = MODULE_TYPE_INFO[mod.type || 'system'];
              const TypeIcon = typeInfo.icon;
              const isCustom = mod.type && mod.type !== 'system';
              const cols = mod.grid_columns || 12;

              return (
                <Paper
                  key={mod.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  variant="outlined"
                  sx={{
                    p: 2.2,
                    borderRadius: 2.5,
                    bgcolor: mod.enabled ? '#FFFFFF' : '#F8FAFC',
                    opacity: mod.enabled ? 1 : 0.65,
                    border: '1px solid',
                    borderColor: draggedIdx === index ? '#2563EB' : '#E2E8F0',
                    boxShadow: draggedIdx === index ? '0 8px 24px rgba(37,99,235,0.18)' : 'none',
                    transition: 'all 0.18s ease',
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                    {/* Drag Handle & Order Badge */}
                    <Grid size={{ xs: 12, sm: 'auto' }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', cursor: 'grab' }}>
                        <GripVertical size={20} />
                      </Box>
                      <Chip
                        label={`ลำดับที่ ${mod.order}`}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: mod.enabled ? '#EFF6FF' : '#E2E8F0',
                          color: mod.enabled ? '#1E40AF' : '#64748B',
                        }}
                      />
                    </Grid>

                    {/* Module Details */}
                    <Grid size={{ xs: 12, sm: 4.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                        <TypeIcon size={16} color={typeInfo.color} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>
                          {mod.name}
                        </Typography>
                        {isCustom && (
                          <Chip
                            label="กำหนดเอง"
                            size="small"
                            sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#FEF3C7', color: '#B45309', fontWeight: 700 }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                        {mod.description}
                      </Typography>
                      {mod.custom_title && (
                        <Typography variant="caption" sx={{ color: '#1E40AF', fontWeight: 600, display: 'block', mt: 0.3 }}>
                          หัวข้อหน้าแรก: "{mod.custom_title}"
                        </Typography>
                      )}
                    </Grid>

                    {/* Column Width Selector (CSS Grid) */}
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="การจัดวางคอลัมน์ (CSS Grid)"
                        value={cols}
                        onChange={(e) => handleChangeColumns(mod.id, Number(e.target.value) as any)}
                        disabled={!mod.enabled}
                        sx={{ bgcolor: '#FFFFFF' }}
                      >
                        {GRID_COLUMN_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Switch & Action Controls */}
                    <Grid size={{ xs: 6, sm: 'auto' }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, ml: 'auto' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={mod.enabled}
                            onChange={() => handleToggleModule(mod.id)}
                            color="primary"
                          />
                        }
                        label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{mod.enabled ? 'เปิดแสดง' : 'ปิดซ่อน'}</Typography>}
                        sx={{ mr: 1 }}
                      />

                      {/* Edit Button */}
                      <Tooltip title="แก้ไขข้อมูลโมดูล">
                        <IconButton size="small" color="primary" onClick={() => handleOpenEditModule(mod)}>
                          <Edit2 size={16} />
                        </IconButton>
                      </Tooltip>

                      {/* Delete Custom Module */}
                      {isCustom && (
                        <Tooltip title="ลบโมดูลกำหนดเอง">
                          <IconButton size="small" color="error" onClick={() => handleDeleteCustomModule(mod.id, mod.name)}>
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Up / Down Move Buttons */}
                      <IconButton
                        size="small"
                        onClick={() => handleMove(index, 'up')}
                        disabled={index === 0}
                        sx={{ color: '#64748B' }}
                      >
                        <ChevronUp size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMove(index, 'down')}
                        disabled={index === modules.length - 1}
                        sx={{ color: '#64748B' }}
                      >
                        <ChevronDown size={18} />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 1: DOWNLOADABLE DOCUMENTS & MANUALS MANAGEMENT */}
      {/* ========================================================================= */}
      {activeTab === 1 && (
        <Box>
          {/* Top Actions: Add Document & Add Category */}
          <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2.5, bgcolor: '#FFFFFF', border: '1px solid #E2E8F0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  คลังเอกสาร คู่มือ และแบบฟอร์มดาวน์โหลดประจำระบบ TPMS
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B' }}>
                  จัดการไฟล์เอกสาร คำร้อง ระเบียบ และคู่มือที่เปิดให้นักศึกษา อาจารย์ และบุคคลทั่วไปสามารถดาวน์โหลดได้
                </Typography>
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FolderOpen size={16} />}
                  onClick={() => handleOpenCategoryDialog()}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  จัดการหมวดหมู่ ({categories.length})
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Plus size={16} />}
                  onClick={() => handleOpenResourceDialog()}
                  sx={{ borderRadius: 2, fontWeight: 700 }}
                >
                  อัปโหลดเอกสารใหม่
                </Button>
              </Stack>
            </Box>
          </Paper>

          {/* Categories Pill Summary */}
          {categories.length > 0 && (
            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', mr: 1 }}>
                หมวดหมู่:
              </Typography>
              {categories.map((c) => (
                <Chip
                  key={c.id}
                  label={c.name}
                  onClick={() => handleOpenCategoryDialog(c)}
                  onDelete={() => handleDeleteCategory(c.id, c.name)}
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: '#FFFFFF' }}
                />
              ))}
            </Box>
          )}

          {/* Search & Filter Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="ค้นหาชื่อเอกสาร หรือรายละเอียด..."
              value={resourceSearch}
              onChange={(e) => setResourceSearch(e.target.value)}
              sx={{ width: { xs: '100%', sm: 300 }, bgcolor: '#FFFFFF' }}
              slotProps={{
                input: {
                  startAdornment: <Search size={16} color="#64748B" style={{ marginRight: 8 }} />,
                },
              }}
            />
            <TextField
              select
              size="small"
              value={selectedCatFilter}
              onChange={(e) => setSelectedCatFilter(e.target.value)}
              sx={{ width: 200, bgcolor: '#FFFFFF' }}
            >
              <MenuItem value="ALL">ทุกหมวดหมู่ (ทั้งหมด)</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Resources Table */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5, bgcolor: '#FFFFFF' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>ชื่อเอกสาร / แบบฟอร์ม</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>หมวดหมู่</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>รายละเอียด</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>ดาวน์โหลด</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>การจัดการ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredResources.map((res) => (
                  <TableRow key={res.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <FileText size={18} color="#1E40AF" />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                            {res.title}
                          </Typography>
                          {res.file_url && (
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              <a href={resolveFileUrl(res.file_url)} target="_blank" rel="noopener noreferrer">
                                เปิดดูไฟล์
                              </a>
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={res.category_name || 'เอกสารทั่วไป'} size="small" sx={{ bgcolor: '#F1F5F9' }} />
                    </TableCell>
                    <TableCell sx={{ color: '#475569', fontSize: '0.88rem', maxWidth: 300 }}>
                      {res.description || '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={`${res.download_count || 0} ครั้ง`} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'center' }}>
                        <IconButton size="small" color="primary" onClick={() => handleOpenResourceDialog(res)}>
                          <Edit2 size={16} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteResource(res.id, res.title)}>
                          <Trash2 size={16} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredResources.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#94A3B8' }}>
                      ยังไม่มีรายการเอกสารในหมวดหมู่นี้
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: PARTNER SCHOOLS DIRECTORY VIEWER */}
      {/* ========================================================================= */}
      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                ข้อมูลโรงเรียนเครือข่ายความร่วมมือฝึกประสบการณ์วิชาชีพครู (พบ {filteredSchools.length} แห่ง)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                รายชื่อโรงเรียนที่เปิดรับนักศึกษาฝึกประสบการณ์วิชาชีพครู คณะครุศาสตร์ มรภ.กำแพงเพชร
              </Typography>
            </Box>

            <TextField
              size="small"
              placeholder="ค้นหาชื่อโรงเรียน หรือจังหวัด..."
              value={schoolSearch}
              onChange={(e) => setSchoolSearch(e.target.value)}
              sx={{ width: 320, bgcolor: '#FFFFFF' }}
              slotProps={{
                input: {
                  startAdornment: <Search size={16} color="#64748B" style={{ marginRight: 8 }} />,
                },
              }}
            />
          </Box>

          <Grid container spacing={2}>
            {filteredSchools.map((s) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.id}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: '#FFFFFF' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Building2 size={20} color="#059669" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A' }}>
                      {s.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#64748B', mb: 0.5 }}>
                    จังหวัด: {s.province || '-'} • อำเภอ: {s.district || '-'}
                  </Typography>
                  {s.phone && (
                    <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block' }}>
                      เบอร์โทร: {s.phone}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* ========================================================================= */}
      {/* DIALOG: ADD / EDIT MODULE (CUSTOM OR SYSTEM) */}
      {/* ========================================================================= */}
      <Dialog
        open={moduleDialogOpen}
        onClose={() => setModuleDialogOpen(false)}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #E2E8F0' }}>
          {editingModule ? `แก้ไขโมดูล: ${editingModule.name}` : 'เพิ่มโมดูลใหม่สำหรับหน้าแรก'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="ชื่อโมดูล (สำหรับจัดการ)"
              fullWidth
              size="small"
              value={moduleForm.name}
              onChange={(e) => setModuleForm({ ...moduleForm, name: e.target.value })}
              required
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="ประเภทโมดูล"
                  fullWidth
                  size="small"
                  value={moduleForm.type}
                  disabled={Boolean(editingModule && editingModule.type === 'system')}
                  onChange={(e) => setModuleForm({ ...moduleForm, type: e.target.value as ModuleType })}
                >
                  <MenuItem value="custom_html">ข้อความ/เนื้อหาอิสระ (Rich Text / Notice)</MenuItem>
                  <MenuItem value="custom_banner">แบนเนอร์ประชาสัมพันธ์พิเศษ (Banner Card)</MenuItem>
                  <MenuItem value="custom_links">การ์ดลิงก์ทางลัดด่วน (Quick Links Grid)</MenuItem>
                  <MenuItem value="custom_video">วิดีโอแนะนำ (YouTube / Video Embed)</MenuItem>
                  {editingModule?.type === 'system' && (
                    <MenuItem value="system">โมดูลระบบมาตรฐาน</MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="ขนาดคอลัมน์ (CSS Grid Layout)"
                  fullWidth
                  size="small"
                  value={moduleForm.grid_columns}
                  onChange={(e) =>
                    setModuleForm({ ...moduleForm, grid_columns: Number(e.target.value) as any })
                  }
                >
                  {GRID_COLUMN_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <TextField
              label="หัวข้อที่จะแสดงบนหน้าแรก (Section Title)"
              fullWidth
              size="small"
              value={moduleForm.custom_title}
              onChange={(e) => setModuleForm({ ...moduleForm, custom_title: e.target.value })}
              placeholder="เช่น ประกาศสำคัญประจำสัปดาห์, แหล่งเรียนรู้แนะนำ"
            />

            <TextField
              label="หัวข้อย่อย / คำโปรย (Subtitle)"
              fullWidth
              size="small"
              value={moduleForm.custom_subtitle}
              onChange={(e) => setModuleForm({ ...moduleForm, custom_subtitle: e.target.value })}
              placeholder="คำอธิบายสั้นๆ เพิ่มเติมใต้หัวข้อ"
            />

            {/* Type Specific Fields */}
            {moduleForm.type === 'custom_html' && (
              <TextField
                label="เนื้อหาข้อความ หรือ HTML (Content)"
                fullWidth
                multiline
                rows={5}
                value={moduleForm.content_html}
                onChange={(e) => setModuleForm({ ...moduleForm, content_html: e.target.value })}
                placeholder="ระบุข้อความ หรือโค้ด HTML ที่ต้องการแสดงในการ์ดหน้านี้..."
              />
            )}

            {moduleForm.type === 'custom_banner' && (
              <>
                <TextField
                  label="ลิงก์รูปภาพแบนเนอร์ (Image URL)"
                  fullWidth
                  size="small"
                  value={moduleForm.banner_url}
                  onChange={(e) => setModuleForm({ ...moduleForm, banner_url: e.target.value })}
                  placeholder="https://... หรือ /uploads/..."
                />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="ลิงก์ปลายทางเมื่อคลิก (Link URL)"
                      fullWidth
                      size="small"
                      value={moduleForm.banner_link_url}
                      onChange={(e) => setModuleForm({ ...moduleForm, banner_link_url: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="ข้อความบนปุ่มกด (Button CTA)"
                      fullWidth
                      size="small"
                      value={moduleForm.banner_cta_text}
                      onChange={(e) => setModuleForm({ ...moduleForm, banner_cta_text: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            {moduleForm.type === 'custom_links' && (
              <TextField
                label="รายการลิงก์ทางลัด (1 บรรทัดต่อ 1 ลิงก์ รูปแบบ: ชื่อลิงก์ | URL | คำอธิบาย)"
                fullWidth
                multiline
                rows={4}
                value={moduleForm.links_text}
                onChange={(e) => setModuleForm({ ...moduleForm, links_text: e.target.value })}
                placeholder={'ระบบประเมินออนไลน์ | https://... | เข้าสู่ระบบประเมิน\nคู่มือนักศึกษา | /uploads/... | ดาวน์โหลด PDF'}
                helperText="คั่นแต่ละส่วนด้วยเครื่องหมาย | เช่น: ชื่อลิงก์ | ลิงก์ URL | คำอธิบายย่อย"
              />
            )}

            {moduleForm.type === 'custom_video' && (
              <TextField
                label="ลิงก์วิดีโอ (YouTube Embed URL หรือลิงก์วิดีโอ)"
                fullWidth
                size="small"
                value={moduleForm.video_url}
                onChange={(e) => setModuleForm({ ...moduleForm, video_url: e.target.value })}
                placeholder="https://www.youtube.com/embed/..."
              />
            )}

            <TextField
              label="คำอธิบายสำหรับผู้ดูแลระบบ (บันทึกภายใน)"
              fullWidth
              size="small"
              value={moduleForm.description}
              onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid #E2E8F0' }}>
          <Button onClick={() => setModuleDialogOpen(false)} color="inherit">
            ยกเลิก
          </Button>
          <Button onClick={handleSaveModuleForm} variant="contained" color="primary">
            บันทึกโมดูล
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* DIALOG: UPLOAD / EDIT RESOURCE */}
      {/* ========================================================================= */}
      <Dialog
        open={resourceDialogOpen}
        onClose={() => setResourceDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingResource ? 'แก้ไขข้อมูลเอกสาร' : 'อัปโหลดเอกสารใหม่'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="ชื่อเอกสาร / แบบฟอร์ม"
              fullWidth
              size="small"
              value={resourceFormData.title}
              onChange={(e) => setResourceFormData({ ...resourceFormData, title: e.target.value })}
              required
            />
            <TextField
              select
              label="หมวดหมู่เอกสาร"
              fullWidth
              size="small"
              value={resourceFormData.category_id}
              onChange={(e) => setResourceFormData({ ...resourceFormData, category_id: e.target.value })}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="คำอธิบายเพิ่มเติม"
              fullWidth
              multiline
              rows={2}
              size="small"
              value={resourceFormData.description}
              onChange={(e) => setResourceFormData({ ...resourceFormData, description: e.target.value })}
            />
            <Button
              variant="outlined"
              component="label"
              startIcon={<Download size={18} />}
              sx={{ py: 1.8, borderStyle: 'dashed', borderRadius: 2 }}
            >
              {resourceFile
                ? resourceFile.name
                : editingResource
                ? 'เลือกไฟล์ใหม่เพื่อแทนที่ (ถ้าต้องการ)'
                : 'เลือกไฟล์ที่ต้องการอัปโหลด (PDF, Word, Excel, ZIP)'}
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
                onChange={(e) => setResourceFile(e.target.files?.[0] || null)}
              />
            </Button>
            {editingResource?.file_url && !resourceFile && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#64748B' }}>
                ไฟล์ปัจจุบัน:{' '}
                <a href={resolveFileUrl(editingResource.file_url)} target="_blank" rel="noopener noreferrer">
                  คลิกเพื่อดูไฟล์เดิม
                </a>
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setResourceDialogOpen(false)} disabled={savingResource}>
            ยกเลิก
          </Button>
          <Button onClick={handleSaveResource} variant="contained" disabled={savingResource}>
            {savingResource ? 'กำลังอัปโหลด...' : 'บันทึกเอกสาร'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* DIALOG: ADD / EDIT CATEGORY */}
      {/* ========================================================================= */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        disableRestoreFocus
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingCategory ? 'แก้ไขหมวดหมู่' : 'เพิ่มหมวดหมู่เอกสาร'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="ชื่อหมวดหมู่"
              fullWidth
              size="small"
              value={catFormData.name}
              onChange={(e) => setCatFormData({ ...catFormData, name: e.target.value })}
              required
            />
            <TextField
              label="คำอธิบายหมวดหมู่"
              fullWidth
              size="small"
              value={catFormData.description}
              onChange={(e) => setCatFormData({ ...catFormData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCategoryDialogOpen(false)}>ยกเลิก</Button>
          <Button onClick={handleSaveCategory} variant="contained">
            บันทึกหมวดหมู่
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LandingPageManager;
